import React, { useState } from "react";
import AddWordPopover from "./AddWordPopover";

const ARABIC = /[؀-ۿ]/;
// Strip leading/trailing non-Arabic (punctuation, latin, digits) off a token.
const cleanWord = (raw) => (raw || "").replace(/^[^؀-ۿ]+|[^؀-ۿ]+$/g, "");

/**
 * TappableArabic — renders an Arabic string where each word is individually
 * tappable. Tapping a word opens an AddWordPopover so the learner can add that
 * word to their Word Bank (AI translate + add via the text path).
 *
 * Drop-in replacement for a <p>{arabicText}</p> in chat bubbles.
 *
 * Props:
 *   text   — the Arabic string to render
 *   as     — wrapper element/tag (default "p")
 *   plus className / style / dir / lang passed to the wrapper.
 */
export default function TappableArabic({ text, as: Tag = "p", className, style, dir = "rtl", lang = "ar" }) {
  const [popover, setPopover] = useState(null); // { word, rect } | null

  const openFor = (raw, el) => {
    const word = cleanWord(raw);
    if (!word || !el) return;
    setPopover({ word, rect: el.getBoundingClientRect() });
  };

  // Split on whitespace but keep the separators so spacing is preserved.
  const tokens = (text || "").split(/(\s+)/);

  return (
    <>
      <Tag dir={dir} lang={lang} className={className} style={style}>
        {tokens.map((tok, i) =>
          ARABIC.test(tok) ? (
            <span
              key={i}
              role="button"
              tabIndex={0}
              title="Add to Word Bank"
              onClick={(e) => openFor(tok, e.currentTarget)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openFor(tok, e.currentTarget);
                }
              }}
              className="cursor-pointer rounded px-0.5 -mx-0.5 transition-colors hover:bg-gold-500/25"
            >
              {tok}
            </span>
          ) : (
            <React.Fragment key={i}>{tok}</React.Fragment>
          )
        )}
      </Tag>
      {popover && (
        <AddWordPopover
          word={popover.word}
          anchorRect={popover.rect}
          onClose={() => setPopover(null)}
        />
      )}
    </>
  );
}
